<template>
  <form id="ar20Antrag">
    <div class="my-4">
      <div class="bgTRnoGrid px-4 px-lg-5 py-4">
            
        <!-- OBERER BEREICH: Tarifauswahl (immer sichtbar) -->
        <div class="boxTR px-4 px-lg-5 py-3 py-lg-4 w-100">
          <h3 class="pb-3">Bitte Tarif auswählen</h3>
          <div class="btnbox d-flex justify-content-center align-items-center flex-column flex-lg-row row pb-3">
            <!-- Globaler AR20 Button mit initialem Preis -->
            <AuslandsversicherungButton 
              class="col-12 col-lg-6 pb-3 pb-lg-0"
              :selected="tarifAusgewaehlt"
              :beitragStr="initialerPreisStr"
              @select="toggleTarif"
            />
            <div class="col-12 col-lg-6">
              <RdnummerLarge v-if="shouldShowRdnummer" />
              <RdnummerOk v-if="shouldShowRdnummerInfo" :rdnummer="store.aok" />
            </div>
          </div>
        </div>

        <!-- UNTERER BEREICH: Nur sichtbar wenn Tarif ausgewählt -->
        <template v-if="tarifAusgewaehlt">
          
          <!-- Versicherungsbeginn -->
          <div class="row">
            <div class="mt-4 mb-2 col-12 col-md-6 col-lg-3">
              <DateTextPickerField
                id="versbeginnAr20"
                label="Versicherungsbeginn *"
                :modelValue="selectedVersicherungsbeginn"
                @update:modelValue="updateVersicherungsbeginn"
              />
            </div>
            <div class="mt-4 mb-2 col-12 col-lg-9">
            </div>
          </div>
          
          <!-- Personen-Iteration -->
          <div v-for="item in store.items" :key="item.id" class="boxTR mb-4 p-3">
            <div class="row g-3">
              <div class="col-12">
                <div class="row">
                  <!-- Geburtsdatum -->
                  <div class="col-12 col-md-3">
                    <DateTextPickerField
                      :modelValue="item.geburtstag"
                      @update:modelValue="(val) => updateGeburtstag(item.id, val)"
                      :id="`vpGeburtstag${item.id}`"
                      label="Geburtsdatum *"
                      required
                    />
                  </div>
                </div>
              </div>

              <!-- Hidden Checkbox für Tarif-Validierung -->
              <input
                type="checkbox"
                hidden
                class="form-check-input"
                :id="`vpTarif${item.id}`"
                :name="`vpTarif${item.id}`"
                v-model="item.hasTarif"
              >

              <!-- Person-Nummer, Entfernen-Button und Beitragsanzeige -->
              <div class="col-12 d-flex flex-column-reverse flex-md-row justify-content-between gap-3">
                <div class="d-flex flex-column align-items-center me-md-3 align-self-center align-self-md-start">
                  <Ar20PersonNummer :index="store.items.indexOf(item)" />
                  <div class="mt-2">
                    <PersonEntfernen 
                      v-if="store.items.length >= 2" 
                      @click="personEntfernen(item.id)" 
                    />
                  </div>
                </div>
                
                <!-- Beitragsanzeige (berechneter Preis pro Person) -->
                <Ar20Person :item="item" class="align-self-end align-self-md-center" />
              </div>
            </div>
          </div>

          <!-- Gesamt-Anzeige mit Button für weitere Person -->
          <Ar20GesamtBeitrag 
            :maxPersonen="10"
            @weitere-person="personHinzufuegen" 
          />

        </template>

      </div>
    </div>
    
    <!-- Fehleranzeige bei API-Fehler -->
    <FehleranzeigeKlein 
      v-if="antragIdError" 
      :message="antragIdError" 
      class="mb-3"
    />
    
    <!-- Weiter-Button nur wenn Tarif ausgewählt -->
    <div v-if="tarifAusgewaehlt" class="d-flex justify-content-end">
      <OnlineAbschliessen @click="handleWeiter" />
    </div>
    
    <!-- Loading-Anzeige -->
    <p v-if="isGeneratingAntragId" class="text-end text-muted mt-2 font-sm-links">
      <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
      Antragsnummer wird generiert...
    </p>
    
    <p v-if="tarifAusgewaehlt" class="text-end text-muted mt-2 font-sm-links">* Pflichtfeld</p>
  </form>
</template>

<script setup>
import { computed, ref, nextTick, onMounted, watch } from 'vue';
import { formValidation } from "@form-validation/bundle/popular";
import { Bootstrap5 } from "@form-validation/plugin-bootstrap5";
import { Trigger } from "@form-validation/plugin-trigger";
import { SubmitButton } from "@form-validation/plugin-submit-button";
import moment from "moment";

import AuslandsversicherungButton from '@/components/ar20/ar20_auslandsversicherung_button.vue';
import DateTextPickerField from '@/fields/DateTextPickerField.vue';
import Ar20PersonNummer from '@/components/ar20/ar20_person_nummer.vue';
import Ar20Person from '@/components/ar20/ar20_person.vue';
import Ar20GesamtBeitrag from '@/components/ar20/ar20_gesamt_beitrag.vue';
import PersonEntfernen from '@/components/general/person_entfernen.vue';
import OnlineAbschliessen from '@/components/general/online_abschliessen.vue';
import RdnummerLarge from '@/components/general/rdnummer_large.vue';
import RdnummerOk from '@/components/general/rdnummer_ok.vue';
import FehleranzeigeKlein from '@/components/general/fehleranzeige_klein.vue';

import { useRechnerStore } from '@/stores/rechner';
import { useMainStore } from '@/stores/useMainStore.js';
import { useAntragStore } from '@/stores/antrag.js';
import { useTarifinfosStore } from '@/stores/tarifinfos.js';

const store = useRechnerStore();
const mainStore = useMainStore();
const antragStore = useAntragStore();
const tarifinfosStore = useTarifinfosStore();

// FormValidation Instanz
let fv = null;
let isInitializing = false; // Verhindert gleichzeitige Initialisierung

// API Call Status
const isGeneratingAntragId = ref(false);
const antragIdError = ref('');

// Prüft ob RD-Nummer-Dialog (Button) angezeigt werden soll
const shouldShowRdnummer = computed(() => {
  return mainStore.aokEnabled && store.aok === 0;
});

// Prüft ob RD-Nummerninfo angezeigt werden soll
const shouldShowRdnummerInfo = computed(() => {
  return mainStore.aokEnabled && store.aok !== 0;
});

// Prüft ob AR20 für mindestens eine Person ausgewählt ist
const tarifAusgewaehlt = computed(() => store.hatAR20());

// Initialer Preis aus API (immer "ab X €" Format)
const initialerPreisStr = computed(() => {
  const variante = mainStore.currentVariante; // 'AR20'
  const preis = tarifinfosStore.getInitialerPreis(variante, 'AR20');
  if (preis) {
    return `ab ${preis.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €`;
  }
  return 'ab 11,40 €';
});

// FormValidation entfernen
const removeFormValidation = () => {
  if (fv) {
    try {
      fv.destroy();
    } catch (error) {
      console.error('[ar20_schritt0] Fehler beim Zerstören der FormValidation:', error);
    }
    fv = null;
  }
  isInitializing = false;
};

// Toggle: Setzt tarif6 für ALLE Personen auf 1 oder 0
const toggleTarif = () => {
  const newValue = tarifAusgewaehlt.value ? 0 : 1;
  store.items.forEach(item => {
    item.tarif6 = newValue;
    item.hasTarif = newValue === 1;
  });
  store.calculateAll();
  
  // FormValidation entfernen wenn Tarif abgewählt wird
  if (newValue === 0 && fv) {
    removeFormValidation();
  }
  
  // FormValidation initialisieren wenn Tarif ausgewählt wird
  // Warte auf Vue-Rendering mit mehreren nextTick
  if (newValue === 1 && !fv) {
    // Warte auf Vue-Rendering - mehrere nextTick für verschachtelte Komponenten
    nextTick(() => {
      nextTick(() => {
        initFormValidation();
      });
    });
  }
};

// Geburtsdatum-Validierung (Vergangenheit, max 105 Jahre)
const maxDateGeburt = moment();
const minDateGeburt = moment().subtract(105, 'years');
const geburtstagsValidation = {
  notEmpty: {
    message: 'Bitte wählen Sie ein Geburtsdatum aus.',
  },
  date: {
    format: 'YYYY-MM-DD',
    max: maxDateGeburt.format('YYYY-MM-DD'),
    min: minDateGeburt.format('YYYY-MM-DD'),
    message: `Das Datum muss zwischen ${minDateGeburt.format('DD.MM.YYYY')} und ${maxDateGeburt.format('DD.MM.YYYY')} liegen.`,
  },
};

// Versicherungsbeginn-Validierung (heute bis max 6 Monate Zukunft)
const minDateVersbeginn = moment().startOf('day');
const maxDateVersbeginn = moment().add(6, 'months').startOf('day');
const versicherungsbeginnValidation = {
  notEmpty: {
    message: 'Bitte wählen Sie einen Versicherungsbeginn aus.',
  },
  date: {
    format: 'YYYY-MM-DD',
    min: minDateVersbeginn.format('YYYY-MM-DD'),
    max: maxDateVersbeginn.format('YYYY-MM-DD'),
    message: `Das Datum muss zwischen ${minDateVersbeginn.format('DD.MM.YYYY')} und ${maxDateVersbeginn.format('DD.MM.YYYY')} liegen.`,
  },
};

// Validierungsregeln pro Person generieren
function generateItemValidationRules(item) {
  return {
    [`vpGeburtstag${item.id}`]: {
      validators: geburtstagsValidation
    },
    [`vpTarif${item.id}`]: {
      validators: {
        callback: {
          message: 'Bitte wählen Sie den Tarif aus.',
          callback: () => store.isTarifValid(item.id)
        }
      }
    }
  };
}

// FormValidation initialisieren
const initFormValidation = () => {
  // Verhindere mehrfache Initialisierung
  if (fv) {
    return;
  }
  
  if (isInitializing) {
    return;
  }
  
  isInitializing = true;
  
  const formElement = document.getElementById('ar20Antrag');
  if (!formElement) {
    isInitializing = false;
    return;
  }
  
  const fields = {
    versbeginnAr20: {
      validators: versicherungsbeginnValidation
    }
  };
  
  store.items.forEach(item => {
    Object.assign(fields, generateItemValidationRules(item));
  });
  
  // Prüfe ob alle Felder im DOM existieren
  const missingFields = [];
  Object.keys(fields).forEach(fieldId => {
    const fieldElement = document.getElementById(fieldId);
    if (!fieldElement) {
      missingFields.push(fieldId);
    }
  });
  
  if (missingFields.length > 0) {
    isInitializing = false;
    // Versuche es nochmal nach kurzer Verzögerung
    setTimeout(() => {
      initFormValidation();
    }, 100);
    return;
  }
  
  try {
    fv = formValidation(formElement, {
      fields,
      plugins: {
        bootstrap5: new Bootstrap5(),
        submitButton: new SubmitButton(),
        trigger: new Trigger({
          event: 'blur change',
        }),
      },
    });
    
    isInitializing = false;
  } catch (error) {
    console.error('[ar20_schritt0] FEHLER bei FormValidation-Initialisierung:', error);
    fv = null;
    isInitializing = false;
  }
};

// Validierung für neue Person hinzufügen
function addItemValidation(id) {
  if (!fv) return;
  fv.addField(`vpGeburtstag${id}`, {
    validators: geburtstagsValidation
  });
  fv.addField(`vpTarif${id}`, {
    validators: {
      callback: {
        message: 'Bitte wählen Sie den Tarif aus.',
        callback: () => store.isTarifValid(id)
      }
    }
  });
}

// Validierung für Person entfernen
function removeItemValidation(id) {
  if (!fv) return;
  fv.removeField(`vpGeburtstag${id}`);
  fv.removeField(`vpTarif${id}`);
}

const selectedVersicherungsbeginn = computed({
  get: () => store.versicherungsbeginn || '',
  set: (value) => {
    store.versicherungsbeginn = value;
    store.calculateAll();
  }
});

// Versicherungsbeginn aktualisieren
const updateVersicherungsbeginn = (value) => {
  store.versicherungsbeginn = value;
  store.calculateAll();
  // Revalidiere das Feld nach Update
  if (fv) {
    nextTick(() => {
      fv.revalidateField('versbeginnAr20');
    });
  }
};

// Aktualisiert das Geburtsdatum und triggert Neuberechnung
const updateGeburtstag = (itemId, value) => {
  const item = store.items.find(i => i.id === itemId);
  if (item) {
    item.geburtstag = value;
    store.setItem(itemId, item);
    // Revalidiere das Feld nach Update
    if (fv) {
      nextTick(() => {
        fv.revalidateField(`vpGeburtstag${itemId}`);
      });
    }
  }
};

// Person hinzufügen (max 10, mit tarif6 = 1)
const personHinzufuegen = () => {
  if (store.items.length < 10) {
    store.addItemBasedOnLast();
    const newItem = store.items.at(-1);
    newItem.tarif6 = 1;  // AR20 automatisch für neue Person
    newItem.hasTarif = true;
    store.calculateAll();
    nextTick(() => {
      addItemValidation(newItem.id);
    });
  }
};

// Person entfernen
const personEntfernen = (itemId) => {
  removeItemValidation(itemId);
  store.deleteItem(itemId);
};

// Submit-Handler
const handleWeiter = async (event) => {
  event.preventDefault();
  
  // Verhindere mehrfache Klicks während API Call läuft
  if (isGeneratingAntragId.value) {
    return;
  }
  
  if (fv) {
    const button = document.querySelector('[data-shake]');
    fv.validate().then(async (status) => {
      if (status === 'Valid') {
        // Formular ist valide → AntragId generieren
        antragIdError.value = '';
        isGeneratingAntragId.value = true;
        
        try {
          const success = await antragStore.generateAntragId();
          
          if (success && antragStore.hasValidAntragId) {
            // AntragId erfolgreich generiert → Weiter zu Schritt 1
            // MATOMO-DEAKTIVIERT (TODO: reaktivieren)
            //window._paq?.push(['trackEvent', 'AR20', 'AR20_Start', 'JetztOnlineAbschliessen']);
            store.stepStatus = 1;
          } else {
            // Fehler beim Generieren der AntragId
            antragIdError.value = antragStore.nachricht || 'Fehler beim Generieren der Antragsnummer. Bitte versuchen Sie es erneut.';
            console.error('[ar20_schritt0] Fehler bei AntragId-Generierung:', antragStore.returnCode, antragStore.nachricht);
            
            if (button) {
              button.classList.add('shake');
              setTimeout(() => button.classList.remove('shake'), 500);
            }
          }
        } catch (error) {
          // Exception abfangen
          antragIdError.value = 'Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie es erneut.';
          console.error('[ar20_schritt0] Exception bei AntragId-Generierung:', error);
          
          if (button) {
            button.classList.add('shake');
            setTimeout(() => button.classList.remove('shake'), 500);
          }
        } finally {
          isGeneratingAntragId.value = false;
        }
      } else {
        // Formular nicht valide
        if (button) {
          button.classList.add('shake');
          setTimeout(() => button.classList.remove('shake'), 500);
        }
        nextTick(() => {
          const firstInvalid = document.querySelector('#ar20Antrag .is-invalid');
          if (firstInvalid) {
            firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        });
      }
    });
  }
};

// Watch für den Fall, dass Tarif abgewählt wird (toggleTarif initialisiert bereits)
watch(tarifAusgewaehlt, (isSelected) => {
  if (!isSelected && fv) {
    // Tarif abgewählt - entferne Validierung
    removeFormValidation();
  }
  // Initialisierung erfolgt über toggleTarif, nicht über Watch
});

onMounted(() => {
  // FormValidation initialisieren wenn Tarif bereits ausgewählt ist (z.B. durch sessionStorage)
  if (tarifAusgewaehlt.value && !fv) {
    nextTick(() => {
      nextTick(() => {
        initFormValidation();
      });
    });
  }
});
</script>

<style scoped>
    .introclaim {
        margin-bottom: 1.5rem;
    }
    .platziert {
        position: absolute;
        right:0;
        top:0;
        transform: translate(-20%,-50%);
    }


    .schritt-zaehler-position {
    position: absolute;
    top: 0;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 90vw;
    }

    @media (min-width: 768px) {
    .schritt-zaehler-position {
        width: 80vw;
    }
    }

    @media (min-width: 992px) {
    .schritt-zaehler-position {
        width: 70vw;
    }
    }

    @media (min-width: 1200px) {
    .schritt-zaehler-position {
        width: 60vw;
    }
    }

    h3 {
        color: #000;
        font-family: "Source Sans Pro";
        font-size: 22px;
        font-style: normal;
        font-weight: 700;
        line-height: 160%;
    }
</style>